// useExtractMode.js
import { useState, useCallback, useRef, useEffect } from "react";
import * as THREE from "three";
import { throttle } from "lodash";
import { anatomyConstants } from "../constants/anatomyConstants";

/**
 * Extract Mode Hook (JS)
 *
 * - Draw polygon on mesh surface (mouse/touch raycast) with a live red preview
 * - Split each selected mesh into OUTSIDE + INSIDE (slab around the polygon's plane)
 * - Replace original with OUTSIDE; merge all INSIDE parts into ONE new mesh
 * - Name, color come from hook state; opacity/renderOrder have defaults here
 * - No undo/redo here
 *
 * To enable point capture, pass containerRef and controlsRef.
 */
const useExtractMode = (
  sceneRef, // React ref to THREE.Scene
  cameraRef, // React ref to THREE.Camera
  modelRef, // React ref to a Group/Object3D containing meshes
  selectedModels, // number[] (indices into modelRef.current.children)
  setProcessing, // (bool) => void (optional)
  containerRef, // OPTIONAL: ref to container HTMLElement for events
  controlsRef, // OPTIONAL: TrackballControls (to lock rotation while drawing)
  viewportRef = null // OPTIONAL: ref for viewport dimensions
) => {
  // Mode & drawing state
  const [isExtractMode, setIsExtractMode] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawPoints, setDrawPoints] = useState([]); // WORLD space points
  const MIN_DISTANCE = 0.05;

  // Name for the combined extracted mesh
  const [extractedName, setExtractedName] = useState("Extract");

  // Thickness (symmetric slab around polygon plane)
  const [cutHeight, setCutHeight] = useState(100);

  // Appearance (color shown in modal; opacity/renderOrder default here)
  const [extractColor, setExtractColor] = useState(anatomyConstants.mainColor);

  // Kept for backward compatibility / defaults live here (UI can ignore):
  const [extractOpacity, setExtractOpacity] = useState(1); // default 1
  const [extractRenderOrder, setExtractRenderOrder] = useState(1); // default 1

  const throttledMouseMove = useRef(null);
  const throttledTouchMove = useRef(null);

  // ---------- Helpers: camera normal & preview line ----------
  const getCameraNormalVector = useCallback(() => {
    const n = new THREE.Vector3();
    cameraRef.current && cameraRef.current.getWorldDirection(n);
    return n.normalize();
  }, [cameraRef]);

  const updateDrawingLine = useCallback(
    (points) => {
      const scene = sceneRef.current;
      if (!scene || points.length < 2) return;

      // remove old preview
      if (scene.userData.currentDrawingLine) {
        const prev = scene.userData.currentDrawingLine;
        scene.remove(prev);
        prev.geometry?.dispose?.();
        prev.material?.dispose?.();
      }

      try {
        let preview;

        if (points.length === 2) {
          const direction = new THREE.Vector3().subVectors(
            points[1],
            points[0]
          );
          const distance = direction.length();
          const mid = new THREE.Vector3()
            .addVectors(points[0], points[1])
            .multiplyScalar(0.5);

          const geom = new THREE.CylinderGeometry(0.2, 0.2, distance, 8);
          const mat = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.9,
          });

          preview = new THREE.Mesh(geom, mat);
          preview.position.copy(mid);
          preview.lookAt(points[1]);
          preview.rotateX(Math.PI / 2);
        } else {
          const curve = new THREE.CatmullRomCurve3(
            points,
            false,
            "centripetal",
            0.1
          );
          const tubeGeom = new THREE.TubeGeometry(
            curve,
            Math.min(64, points.length * 8),
            0.25,
            8,
            false
          );
          const tubeMat = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
          });
          preview = new THREE.Mesh(tubeGeom, tubeMat);
        }

        scene.add(preview);
        scene.userData.currentDrawingLine = preview;
      } catch {
        // fallback: tiny spheres per point
        const group = new THREE.Group();
        points.forEach((p) => {
          const sg = new THREE.SphereGeometry(0.3, 8, 8);
          const sm = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.8,
          });
          const s = new THREE.Mesh(sg, sm);
          s.position.copy(p);
          group.add(s);
        });
        scene.add(group);
        scene.userData.currentDrawingLine = group;
      }
    },
    [sceneRef]
  );

  const clearDrawPoints = useCallback(() => {
    setDrawPoints([]);
    const scene = sceneRef.current;
    if (scene?.userData?.currentDrawingLine) {
      const line = scene.userData.currentDrawingLine;
      scene.remove(line);
      line.geometry?.dispose?.();
      line.material?.dispose?.();
      scene.userData.currentDrawingLine = null;
    }
  }, [sceneRef]);

  // ---------- 3D polygon/slab tests & triangle clipping ----------
  const pointInPolygon3D = useCallback(
    (point, polygonPoints, normal, height) => {
      if (!polygonPoints?.length || polygonPoints.length < 3) return false;

      // polygon center (plane origin)
      const center = new THREE.Vector3();
      polygonPoints.forEach((p) => center.add(p));
      center.multiplyScalar(1 / polygonPoints.length);

      // symmetric slab: |(p - center)·n| <= H/2
      const dist = Math.abs(point.clone().sub(center).dot(normal));
      if (dist > height * 0.5) return false;

      // project onto plane basis (u,v)
      const u = new THREE.Vector3();
      const v = new THREE.Vector3();
      if (polygonPoints.length > 1) {
        u.subVectors(polygonPoints[1], polygonPoints[0]).normalize();
      } else {
        u.set(1, 0, 0);
      }
      v.crossVectors(normal, u).normalize();
      const origin = polygonPoints[0];

      // polygon to 2D
      const poly2 = polygonPoints.map((p) => {
        const rel = p.clone().sub(origin);
        return new THREE.Vector2(rel.dot(u), rel.dot(v));
      });

      // point to 2D
      const relp = point.clone().sub(origin);
      const p2 = new THREE.Vector2(relp.dot(u), relp.dot(v));

      // ray-cast in 2D
      let inside = false;
      for (let i = 0, j = poly2.length - 1; i < poly2.length; j = i++) {
        const xi = poly2[i].x,
          yi = poly2[i].y;
        const xj = poly2[j].x,
          yj = poly2[j].y;
        if (
          // eslint-disable-next-line
          yi > p2.y !== yj > p2.y &&
          p2.x < ((xj - xi) * (p2.y - yi)) / (yj - yi) + xi
        ) {
          inside = !inside;
        }
      }
      return inside;
    },
    []
  );

  const interpolateOnEdge = useCallback(
    (v1, v2, n1, n2, polygonPoints, normal, height) => {
      const steps = 100;
      let lastInside = pointInPolygon3D(v1, polygonPoints, normal, height);
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const tp = new THREE.Vector3().lerpVectors(v1, v2, t);
        const isInside = pointInPolygon3D(tp, polygonPoints, normal, height);
        if (isInside !== lastInside) {
          // refine by bisection
          let a = (i - 1) / steps,
            b = t;
          for (let k = 0; k < 6; k++) {
            const m = 0.5 * (a + b);
            const mp = new THREE.Vector3().lerpVectors(v1, v2, m);
            if (
              pointInPolygon3D(mp, polygonPoints, normal, height) === lastInside
            )
              a = m;
            else b = m;
          }
          const ft = 0.5 * (a + b);
          const vertex = new THREE.Vector3().lerpVectors(v1, v2, ft);
          const normalAt = new THREE.Vector3()
            .lerpVectors(n1, n2, ft)
            .normalize();
          return { vertex, normal: normalAt };
        }
        lastInside = isInside;
      }
      return null;
    },
    [pointInPolygon3D]
  );

  const clipTriangleToPolygon = useCallback(
    (v1, v2, v3, n1, n2, n3, polygonPoints, normal, height, keepInside) => {
      const inFlags = [
        pointInPolygon3D(v1, polygonPoints, normal, height),
        pointInPolygon3D(v2, polygonPoints, normal, height),
        pointInPolygon3D(v3, polygonPoints, normal, height),
      ];
      const inCount = inFlags.filter(Boolean).length;

      const keepV = (i) => (keepInside ? inFlags[i] : !inFlags[i]);

      if (inCount === 3) {
        return keepInside
          ? [{ vertices: [v1, v2, v3], normals: [n1, n2, n3] }]
          : [];
      }
      if (inCount === 0) {
        return keepInside
          ? []
          : [{ vertices: [v1, v2, v3], normals: [n1, n2, n3] }];
      }

      const verts = [v1, v2, v3];
      const norms = [n1, n2, n3];
      const edges = [
        [0, 1],
        [1, 2],
        [2, 0],
      ];

      const keptVerts = [];
      const keptNorms = [];

      for (let i = 0; i < 3; i++) {
        if (keepV(i)) {
          keptVerts.push(verts[i]);
          keptNorms.push(norms[i]);
        }
      }

      const intersections = [];
      edges.forEach(([i, j]) => {
        if (inFlags[i] !== inFlags[j]) {
          const inter = interpolateOnEdge(
            verts[i],
            verts[j],
            norms[i],
            norms[j],
            polygonPoints,
            normal,
            height
          );
          if (inter) intersections.push(inter);
        }
      });

      intersections.forEach(({ vertex, normal: nn }) => {
        keptVerts.push(vertex);
        keptNorms.push(nn);
      });

      if (keptVerts.length < 3) return [];

      const tris = [];
      for (let i = 1; i < keptVerts.length - 1; i++) {
        tris.push({
          vertices: [keptVerts[0], keptVerts[i], keptVerts[i + 1]],
          normals: [keptNorms[0], keptNorms[i], keptNorms[i + 1]],
        });
      }
      return tris;
    },
    [pointInPolygon3D, interpolateOnEdge]
  );

  const processTriangleWithSubdivision = useCallback(
    (
      v1,
      v2,
      v3,
      n1,
      n2,
      n3,
      polygonPoints,
      normal,
      height,
      keepInside,
      depth = 0
    ) => {
      const maxDepth = 6;
      const e1 = v1.distanceTo(v2);
      const e2 = v2.distanceTo(v3);
      const e3 = v3.distanceTo(v1);
      const maxE = Math.max(e1, e2, e3);

      // average polygon span (heuristic)
      let polyLen = 0;
      for (let i = 0; i < polygonPoints.length - 1; i++) {
        polyLen += polygonPoints[i].distanceTo(polygonPoints[i + 1]);
      }
      const avgSpan = polyLen / Math.max(1, polygonPoints.length - 1);

      if (depth >= maxDepth || maxE < avgSpan * 0.3) {
        return clipTriangleToPolygon(
          v1,
          v2,
          v3,
          n1,
          n2,
          n3,
          polygonPoints,
          normal,
          height,
          keepInside
        );
      }

      const inFlags = [
        pointInPolygon3D(v1, polygonPoints, normal, height),
        pointInPolygon3D(v2, polygonPoints, normal, height),
        pointInPolygon3D(v3, polygonPoints, normal, height),
      ];
      const inCount = inFlags.filter(Boolean).length;
      if (inCount === 0 || inCount === 3) {
        return clipTriangleToPolygon(
          v1,
          v2,
          v3,
          n1,
          n2,
          n3,
          polygonPoints,
          normal,
          height,
          keepInside
        );
      }

      const m12 = new THREE.Vector3().addVectors(v1, v2).multiplyScalar(0.5);
      const m23 = new THREE.Vector3().addVectors(v2, v3).multiplyScalar(0.5);
      const m31 = new THREE.Vector3().addVectors(v3, v1).multiplyScalar(0.5);

      const mn12 = new THREE.Vector3().addVectors(n1, n2).normalize();
      const mn23 = new THREE.Vector3().addVectors(n2, n3).normalize();
      const mn31 = new THREE.Vector3().addVectors(n3, n1).normalize();

      const quads = [
        [v1, m12, m31, n1, mn12, mn31],
        [m12, v2, m23, mn12, n2, mn23],
        [m31, m23, v3, mn31, mn23, n3],
        [m12, m23, m31, mn12, mn23, mn31],
      ];

      const out = [];
      for (const [a, b, c, na, nb, nc] of quads) {
        out.push(
          ...processTriangleWithSubdivision(
            a,
            b,
            c,
            na,
            nb,
            nc,
            polygonPoints,
            normal,
            height,
            keepInside,
            depth + 1
          )
        );
      }
      return out;
    },
    [clipTriangleToPolygon, pointInPolygon3D]
  );

  const splitGeometryBySelection = useCallback(
    (geometry, basePoints, normal, height) => {
      const polygonPoints = basePoints[0].equals(
        basePoints[basePoints.length - 1]
      )
        ? basePoints
        : [...basePoints, basePoints[0]];

      const nonIndexed = geometry.index ? geometry.toNonIndexed() : geometry;
      const pos = nonIndexed.getAttribute("position");
      const nor = nonIndexed.getAttribute("normal");
      if (!pos || !nor)
        throw new Error("Geometry needs position & normal attributes.");

      const inside = [];
      const outside = [];

      for (let i = 0; i < pos.count; i += 3) {
        const v1 = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
        const v2 = new THREE.Vector3(
          pos.getX(i + 1),
          pos.getY(i + 1),
          pos.getZ(i + 1)
        );
        const v3 = new THREE.Vector3(
          pos.getX(i + 2),
          pos.getY(i + 2),
          pos.getZ(i + 2)
        );

        const n1 = new THREE.Vector3(nor.getX(i), nor.getY(i), nor.getZ(i));
        const n2 = new THREE.Vector3(
          nor.getX(i + 1),
          nor.getY(i + 1),
          nor.getZ(i + 1)
        );
        const n3 = new THREE.Vector3(
          nor.getX(i + 2),
          nor.getY(i + 2),
          nor.getZ(i + 2)
        );

        inside.push(
          ...processTriangleWithSubdivision(
            v1,
            v2,
            v3,
            n1,
            n2,
            n3,
            polygonPoints,
            normal,
            height,
            true
          )
        );
        outside.push(
          ...processTriangleWithSubdivision(
            v1,
            v2,
            v3,
            n1,
            n2,
            n3,
            polygonPoints,
            normal,
            height,
            false
          )
        );
      }

      const makeGeom = (tris) => {
        if (!tris.length) return new THREE.BufferGeometry();
        const positions = new Float32Array(tris.length * 9);
        const normals = new Float32Array(tris.length * 9);
        let k = 0;
        for (const t of tris) {
          for (let i = 0; i < 3; i++) {
            const v = t.vertices[i];
            const n = t.normals[i];
            positions[k] = v.x;
            positions[k + 1] = v.y;
            positions[k + 2] = v.z;
            normals[k] = n.x;
            normals[k + 1] = n.y;
            normals[k + 2] = n.z;
            k += 3;
          }
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        g.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
        g.computeBoundingBox();
        g.computeBoundingSphere();
        g.computeVertexNormals();
        return g;
      };

      return {
        insideGeometry: makeGeom(inside),
        outsideGeometry: makeGeom(outside),
      };
    },
    [processTriangleWithSubdivision]
  );

  // Convert a local-geometry fragment into world-space and append into acc arrays
  function appendWorldSpaceTriangles(acc, geom, mesh) {
    if (!geom || !geom.getAttribute("position")) return;
    const nonIndexed = geom.index ? geom.toNonIndexed() : geom;

    const pos = nonIndexed.getAttribute("position");
    let nor = nonIndexed.getAttribute("normal");

    const m4 = mesh.matrixWorld.clone();
    const n3 = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);

    if (!acc.positions) acc.positions = [];
    if (!acc.normals) acc.normals = [];

    for (let i = 0; i < pos.count; i++) {
      const v = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
      v.applyMatrix4(m4);
      acc.positions.push(v.x, v.y, v.z);

      if (nor) {
        const n = new THREE.Vector3(nor.getX(i), nor.getY(i), nor.getZ(i));
        n.applyMatrix3(n3).normalize();
        acc.normals.push(n.x, n.y, n.z);
      } else {
        acc.normals.push(0, 0, 1);
      }
    }
  }

  // ---------- Apply extract (MERGED INSIDE across all selected) ----------
  const applyExtract = useCallback(() => {
    if (!modelRef?.current || !sceneRef?.current) return;
    if (!drawPoints || drawPoints.length < 3) return;

    setProcessing?.(true);

    const pointsWorld = drawPoints[0].equals(drawPoints[drawPoints.length - 1])
      ? drawPoints
      : [...drawPoints, drawPoints[0]];

    const normalWorld = getCameraNormalVector();
    const merged = { positions: [], normals: [] };

    selectedModels.forEach((modelIndex) => {
      const mesh = modelRef.current.children[modelIndex];
      if (!mesh || !mesh.geometry) return;

      // Convert polygon & normal to LOCAL of this mesh
      const localPoints = pointsWorld.map((p) => mesh.worldToLocal(p.clone()));
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(
        mesh.matrixWorld
      );
      const localNormal = normalWorld
        .clone()
        .applyMatrix3(normalMatrix)
        .normalize();

      // Split
      const { insideGeometry, outsideGeometry } = splitGeometryBySelection(
        mesh.geometry,
        localPoints,
        localNormal,
        cutHeight
      );

      // Replace original with OUTSIDE
      const old = mesh.geometry;
      mesh.geometry = outsideGeometry;
      old.dispose?.();

      // Append INSIDE (world-space) into merged arrays
      const hasInside = insideGeometry.getAttribute("position")?.count > 0;
      if (hasInside) {
        appendWorldSpaceTriangles(merged, insideGeometry, mesh);
        insideGeometry.dispose?.();
      }
    });

    // Create ONE merged mesh under modelRef so ModelsSidebar sees it
    if (merged.positions.length > 0) {
      const posArr = new Float32Array(merged.positions);
      const norArr = new Float32Array(merged.normals);

      const mergedGeom = new THREE.BufferGeometry();
      mergedGeom.setAttribute("position", new THREE.BufferAttribute(posArr, 3));
      mergedGeom.setAttribute("normal", new THREE.BufferAttribute(norArr, 3));
      mergedGeom.computeBoundingBox();
      mergedGeom.computeBoundingSphere();
      mergedGeom.computeVertexNormals();

      const mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(extractColor),
        transparent: true,
        opacity: extractOpacity, // default 1
        depthWrite: extractOpacity === 1, // true at 1, else false
        depthTest: true,
        side: THREE.DoubleSide,
      });

      const nameBase =
        extractedName && extractedName.trim().length
          ? extractedName.trim()
          : "Extract";

      const combined = new THREE.Mesh(mergedGeom, mat);
      combined.name = `${nameBase}_${Date.now()}`;
      combined.userData.sourceKey = combined.name; // lets your sidebar track it
      combined.renderOrder = extractRenderOrder; // default 1
      combined.castShadow = true;
      combined.receiveShadow = true;

      (modelRef.current || sceneRef.current).add(combined);
    }

    // clear preview
    const scene = sceneRef.current;
    if (scene?.userData?.currentDrawingLine) {
      const l = scene.userData.currentDrawingLine;
      scene.remove(l);
      l.geometry?.dispose?.();
      l.material?.dispose?.();
      scene.userData.currentDrawingLine = null;
    }

    setProcessing?.(false);
  }, [
    drawPoints,
    modelRef,
    sceneRef,
    cutHeight,
    extractColor,
    extractOpacity,
    extractRenderOrder,
    selectedModels,
    extractedName,
    getCameraNormalVector,
    splitGeometryBySelection,
    setProcessing,
  ]);

  // ---------- Pointer capture (mouse/touch) ----------
  useEffect(() => {
    if (
      !isExtractMode ||
      !containerRef?.current ||
      !cameraRef.current ||
      !modelRef.current
    ) {
      return;
    }

    const root = containerRef.current;
    const raycaster = new THREE.Raycaster();

    const addPointFromNDC = (nx, ny) => {
      const visibleModels = modelRef.current.children.filter((c) => c.visible);
      if (!visibleModels.length) return;

      raycaster.setFromCamera({ x: nx, y: ny }, cameraRef.current);
      const hits = raycaster.intersectObjects(visibleModels, true);
      if (!hits.length) return;

      const p = hits[0].point;
      if (
        drawPoints.length === 0 ||
        drawPoints[drawPoints.length - 1].distanceTo(p) > MIN_DISTANCE
      ) {
        setDrawPoints((prev) => {
          const next = [...prev, p];
          updateDrawingLine(next);
          return next;
        });
      }
    };

    throttledMouseMove.current = throttle((e) => {
      if (!isDrawing) return;
      if (e.target.closest(".ui-outside-scene")) return;
      const rect = root.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const nx = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const ny = -((mouseY - viewport.top) / viewport.height) * 2 + 1;
      addPointFromNDC(nx, ny);
    }, 16);

    throttledTouchMove.current = throttle((e) => {
      if (!isDrawing) return;
      if (e.target.closest(".ui-outside-scene")) return;
      e.preventDefault();
      const touch = e.touches[0];
      const rect = root.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = touch.clientX - rect.left;
      const mouseY = touch.clientY - rect.top;
      
      const nx = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const ny = -((mouseY - viewport.top) / viewport.height) * 2 + 1;
      addPointFromNDC(nx, ny);
    }, 16);

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (e.target.closest(".ui-outside-scene")) return;
      const rect = root.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const nx = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const ny = -((mouseY - viewport.top) / viewport.height) * 2 + 1;
      const visibleModels = modelRef.current.children.filter((c) => c.visible);
      if (!visibleModels.length) return;

      raycaster.setFromCamera({ x: nx, y: ny }, cameraRef.current);
      const hits = raycaster.intersectObjects(visibleModels, true);
      if (!hits.length) return;

      const p = hits[0].point;
      setDrawPoints([p]);
      setIsDrawing(true);
      if (controlsRef?.current) controlsRef.current.noRotate = true;
    };

    const onMouseMove = (e) =>
      throttledMouseMove.current && throttledMouseMove.current(e);

    const onMouseUp = () => {
      if (!isDrawing) return;
      setIsDrawing(false);
      if (controlsRef?.current) controlsRef.current.noRotate = false;
    };

    const onTouchStart = (e) => {
      if (e.target.closest(".ui-outside-scene")) return;
      e.preventDefault();
      const t = e.touches[0];
      const rect = root.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = t.clientX - rect.left;
      const mouseY = t.clientY - rect.top;
      
      const nx = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const ny = -((mouseY - viewport.top) / viewport.height) * 2 + 1;

      const visibleModels = modelRef.current.children.filter((c) => c.visible);
      if (!visibleModels.length) return;

      raycaster.setFromCamera({ x: nx, y: ny }, cameraRef.current);
      const hits = raycaster.intersectObjects(visibleModels, true);
      if (!hits.length) return;

      const p = hits[0].point;
      setDrawPoints([p]);
      setIsDrawing(true);
      if (controlsRef?.current) controlsRef.current.noRotate = true;
    };

    const onTouchMove = (e) =>
      throttledTouchMove.current && throttledTouchMove.current(e);

    const onTouchEnd = (e) => {
      if (e.target.closest(".ui-outside-scene")) return;
      e.preventDefault();
      if (!isDrawing) return;
      setIsDrawing(false);
      if (controlsRef?.current) controlsRef.current.noRotate = false;
    };

    root.addEventListener("mousedown", onMouseDown);
    root.addEventListener("mousemove", onMouseMove);
    root.addEventListener("mouseup", onMouseUp);
    root.addEventListener("touchstart", onTouchStart, { passive: false });
    root.addEventListener("touchmove", onTouchMove, { passive: false });
    root.addEventListener("touchend", onTouchEnd);

    return () => {
      root.removeEventListener("mousedown", onMouseDown);
      root.removeEventListener("mousemove", onMouseMove);
      root.removeEventListener("mouseup", onMouseUp);
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      root.removeEventListener("touchend", onTouchEnd);
      throttledMouseMove.current?.cancel?.();
      throttledTouchMove.current?.cancel?.();
    };
  }, [
    isExtractMode,
    isDrawing,
    cameraRef,
    modelRef,
    containerRef,
    controlsRef,
    drawPoints,
    updateDrawingLine,
    viewportRef,
  ]);

  return {
    // mode
    isExtractMode,
    setIsExtractMode,

    // drawing inputs
    setDrawPoints,
    clearDrawPoints,

    // naming
    extractedName,
    setExtractedName,

    // geometry/thickness
    cutHeight,
    setCutHeight,

    // appearance (color in UI)
    extractColor,
    setExtractColor,

    // defaults kept here; exporting for backward compatibility
    extractOpacity,
    setExtractOpacity,
    extractRenderOrder,
    setExtractRenderOrder,

    // action
    applyExtract,
  };
};

export default useExtractMode;
